import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCcw,
  Search,
  UploadCloud,
  UserCheck,
} from 'lucide-react';

import api, { getApiErrorMessage } from '../../services/api';
import { hasPermission } from '../../utils/permissions';
import {
  FeedbackBanner,
  LoadingState,
  PageHeader,
  PageLayout,
} from '../../components/ui/PageLayout';

function getErroItem(item) {
  const error = item?.error;
  if (!error) return item?.message || 'Erro não informado.';
  if (typeof error === 'string') return error;
  return error.message || error.error || JSON.stringify(error);
}

function ResultadoLista({ title, items, tone }) {
  if (!items?.length) return null;

  const styles = {
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    info: 'border-sky-200 bg-sky-50 text-sky-800',
    error: 'border-red-200 bg-red-50 text-red-800',
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className={`border-b px-4 py-3 text-sm font-bold ${styles[tone] || styles.info}`}>
        {title} ({items.length})
      </div>
      <div className="max-h-72 overflow-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3 font-semibold">Morador</th>
              <th className="px-4 py-3 font-semibold">Unidade</th>
              <th className="px-4 py-3 font-semibold">Facial</th>
              <th className="px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((item, index) => (
              <tr key={`${item.morador_id}-${item.dispositivo_id}-${index}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{item.morador_nome}</td>
                <td className="px-4 py-3 text-slate-600">{item.unidade || '-'}</td>
                <td className="px-4 py-3 text-slate-600">{item.dispositivo_nome}</td>
                <td className="max-w-md px-4 py-3 text-slate-600">{tone === 'error' ? getErroItem(item) : item.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CadastroMassaPage({ auth }) {
  const [dispositivos, setDispositivos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [moradores, setMoradores] = useState([]);
  const [tipoDestino, setTipoDestino] = useState('grupo');
  const [selectedDestinoId, setSelectedDestinoId] = useState('');
  const [selectedMoradores, setSelectedMoradores] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [somenteComFoto, setSomenteComFoto] = useState(true);
  const [forcarReenvio, setForcarReenvio] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [resultado, setResultado] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [dispositivosResponse, gruposResponse, usuariosResponse] = await Promise.all([
        api.get('/dispositivos/'),
        api.get('/dispositivos/grupos'),
        api.get('/dispositivos/usuarios-opcoes'),
      ]);

      const moradoresData = usuariosResponse.data?.moradores || [];
      setDispositivos(dispositivosResponse.data || []);
      setGrupos(gruposResponse.data || []);
      setMoradores(moradoresData);
      setSelectedMoradores(new Set(moradoresData.filter((morador) => morador.foto_url).map((morador) => morador.id)));
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Erro ao carregar dados do cadastro em massa.') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectedGrupo = grupos.find((grupo) => String(grupo.id) === String(selectedDestinoId));
  const selectedDispositivo = dispositivos.find((dispositivo) => String(dispositivo.id) === String(selectedDestinoId));
  const dispositivosDoGrupo = useMemo(
    () => dispositivos.filter((dispositivo) => dispositivo.grupo_ids?.includes(Number(selectedDestinoId))),
    [dispositivos, selectedDestinoId]
  );
  const dispositivosSelecionados = tipoDestino === 'grupo'
    ? dispositivosDoGrupo
    : selectedDispositivo
      ? [selectedDispositivo]
      : [];

  const moradoresFiltrados = useMemo(() => {
    const termo = searchTerm.trim().toLowerCase();
    return moradores.filter((morador) => {
      if (somenteComFoto && !morador.foto_url) return false;
      if (!termo) return true;
      return (
        morador.nome.toLowerCase().includes(termo) ||
        String(morador.unidade || '').toLowerCase().includes(termo)
      );
    });
  }, [moradores, searchTerm, somenteComFoto]);

  const moradoresSelecionadosLista = moradores.filter((morador) => selectedMoradores.has(morador.id));
  const totalOperacoes = moradoresSelecionadosLista.length * dispositivosSelecionados.length;
  const canSync = hasPermission(auth, 'cadastro_massa', 'criar') && totalOperacoes > 0 && !sincronizando;

  const toggleMorador = (moradorId) => {
    setSelectedMoradores((current) => {
      const next = new Set(current);
      if (next.has(moradorId)) {
        next.delete(moradorId);
      } else {
        next.add(moradorId);
      }
      return next;
    });
  };

  const selecionarFiltrados = () => {
    setSelectedMoradores((current) => {
      const next = new Set(current);
      moradoresFiltrados.forEach((morador) => next.add(morador.id));
      return next;
    });
  };

  const limparSelecaoFiltrada = () => {
    setSelectedMoradores((current) => {
      const next = new Set(current);
      moradoresFiltrados.forEach((morador) => next.delete(morador.id));
      return next;
    });
  };

  const sincronizar = async () => {
    if (!canSync) return;

    const destino = tipoDestino === 'grupo'
      ? `${dispositivosSelecionados.length} facial(is) do grupo ${selectedGrupo?.nome || ''}`
      : selectedDispositivo?.nome || 'facial selecionada';

    const confirmado = window.confirm(`Enviar ${moradoresSelecionadosLista.length} morador(es) para ${destino}? Usuários já sincronizados serão ignorados.`);
    if (!confirmado) return;

    try {
      setSincronizando(true);
      setResultado(null);
      const response = await api.post('/dispositivos/usuarios/moradores/sincronizar', {
        dispositivo_ids: dispositivosSelecionados.map((dispositivo) => dispositivo.id),
        morador_ids: moradoresSelecionadosLista.map((morador) => morador.id),
        somente_com_foto: somenteComFoto,
        forcar_reenvio: forcarReenvio,
      });
      setResultado(response.data);
      setFeedback({
        type: response.data?.falhas?.length ? 'info' : 'success',
        message: response.data?.message || 'Sincronização concluída.',
      });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Erro ao sincronizar moradores nas faciais.') });
    } finally {
      setSincronizando(false);
    }
  };


  return (
    <PageLayout>
      <PageHeader
        title="Cadastro em Massa"
        description="Sincronize moradores em massa com faciais Intelbras e Hikvision sem reenviar quem já existe."
        actions={(
          <button
            type="button"
            onClick={load}
            disabled={loading || sincronizando}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCcw size={18} />
            Atualizar
          </button>
        )}
      />

      <FeedbackBanner type={feedback?.type} message={feedback?.message} onClose={() => setFeedback(null)} />


      {loading ? (
        <LoadingState message="Carregando moradores e faciais..." />
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(320px,420px)_1fr]">
          <div className="space-y-6">
            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-bold text-slate-800">Destino</h2>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setTipoDestino('grupo');
                    setSelectedDestinoId('');
                  }}
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-bold transition ${
                    tipoDestino === 'grupo'
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  Grupo
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTipoDestino('dispositivo');
                    setSelectedDestinoId('');
                  }}
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-bold transition ${
                    tipoDestino === 'dispositivo'
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  Facial
                </button>
              </div>

              <label className="mb-2 mt-5 block text-sm font-semibold text-gray-700">
                {tipoDestino === 'grupo' ? 'Grupo facial' : 'Facial'}
              </label>
              <select
                className="w-full rounded-xl border border-gray-300 bg-white p-3 outline-none"
                value={selectedDestinoId}
                onChange={(event) => setSelectedDestinoId(event.target.value)}
              >
                <option value="">Selecione...</option>
                {tipoDestino === 'grupo'
                  ? grupos.map((grupo) => (
                      <option key={grupo.id} value={grupo.id}>
                        {grupo.nome} - {grupo.total_dispositivos || 0} facial(is)
                      </option>
                    ))
                  : dispositivos.map((dispositivo) => (
                      <option key={dispositivo.id} value={dispositivo.id}>
                        {dispositivo.nome} - {dispositivo.marca || 'sem marca'} - {dispositivo.ip}
                      </option>
                    ))}
              </select>

              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                {dispositivosSelecionados.length
                  ? `${dispositivosSelecionados.length} facial(is) receberão ${moradoresSelecionadosLista.length} morador(es), totalizando ${totalOperacoes} operação(ões).`
                  : 'Selecione um destino com faciais vinculadas.'}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-bold text-slate-800">Execução</h2>
              <label className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={somenteComFoto}
                  onChange={(event) => setSomenteComFoto(event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Enviar somente moradores com foto
              </label>
              <label className="mt-3 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800">
                <input
                  type="checkbox"
                  checked={forcarReenvio}
                  onChange={(event) => setForcarReenvio(event.target.checked)}
                  className="h-4 w-4 rounded border-amber-300"
                />
                Reenviar mesmo se já constar como sincronizado
              </label>
              <button
                type="button"
                disabled={!canSync}
                onClick={sincronizar}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <UploadCloud size={18} />
                {sincronizando ? 'Sincronizando...' : 'Sincronizar selecionados'}
              </button>
            </section>
          </div>

          <section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
            <div className="border-b border-slate-100 p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Moradores</h2>
                  <p className="mt-1 text-sm text-slate-500">{moradoresSelecionadosLista.length} selecionado(s)</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={selecionarFiltrados} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    Selecionar filtrados
                  </button>
                  <button type="button" onClick={limparSelecaoFiltrada} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    Limpar filtrados
                  </button>
                </div>
              </div>
              <div className="relative mt-4">
                <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar por nome ou unidade..."
                  className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-3 outline-none transition focus:ring-2 focus:ring-slate-300"
                />
              </div>
            </div>

            <div className="max-h-[520px] overflow-auto">
              {moradoresFiltrados.length ? moradoresFiltrados.map((morador) => (
                <label key={morador.id} className="flex cursor-pointer items-center gap-4 border-b border-slate-100 px-5 py-4 transition hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selectedMoradores.has(morador.id)}
                    onChange={() => toggleMorador(morador.id)}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-slate-800">{morador.nome}</p>
                    <p className="mt-1 text-xs text-slate-500">Unidade {morador.unidade || '-'}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${morador.foto_url ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {morador.foto_url ? 'Com foto' : 'Sem foto'}
                  </span>
                </label>
              )) : (
                <div className="px-5 py-12 text-center text-sm italic text-slate-400">Nenhum morador encontrado.</div>
              )}
            </div>
          </section>
        </div>
      )}

      {resultado && (
        <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Resultado da sincronização</h2>
              <p className="mt-1 text-sm text-slate-500">{resultado.message}</p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div className="rounded-xl bg-emerald-50 px-4 py-3 text-emerald-800">
                <CheckCircle2 className="mx-auto mb-1" size={18} />
                {resultado.enviados?.length || 0}
              </div>
              <div className="rounded-xl bg-sky-50 px-4 py-3 text-sky-800">
                <UserCheck className="mx-auto mb-1" size={18} />
                {resultado.existentes?.length || 0}
              </div>
              <div className="rounded-xl bg-red-50 px-4 py-3 text-red-800">
                <AlertTriangle className="mx-auto mb-1" size={18} />
                {resultado.falhas?.length || 0}
              </div>
            </div>
          </div>

          <ResultadoLista title="Enviados" items={resultado.enviados} tone="success" />
          <ResultadoLista title="Já existentes" items={resultado.existentes} tone="info" />
          <ResultadoLista title="Falhas" items={resultado.falhas} tone="error" />
        </section>
      )}
    </PageLayout>
  );
}
